import { toThousandths, type KpiDeltaTone, type KpiIconTone } from '@platform/design-system';
import {
  ChartColumn,
  ChartLine,
  DollarSign,
  Hourglass,
  Package,
  PackageX,
  RotateCcw,
  ShoppingCart,
  Tags,
  TrendingUp,
  Truck,
  Wallet,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import type { DashboardStats } from './dashboard-stats.ts';
import type { PerformanceReport, PerformanceSummary, SeriesMetric } from './performance.ts';
import type { PriceRange, StockValueTotals } from './stock-value/stock-value.ts';

export type OverviewFigures = {
  readonly report: PerformanceReport | undefined;
  readonly stats: DashboardStats | undefined;
  readonly stock: StockValueTotals | undefined;
};

export type OverviewFormat = {
  readonly money: (amount: string, currency: string) => string;
  readonly count: (value: number) => string;
  readonly counted: (key: string, value: number, values?: Readonly<Record<string, string>>) => string;
  readonly range: (range: PriceRange, currency: string) => string;
};

export type OverviewTile = {
  readonly key: string;
  readonly icon: LucideIcon;
  readonly tone: KpiIconTone;
  readonly source: keyof OverviewFigures;
  readonly value: (figures: OverviewFigures, format: OverviewFormat) => string | undefined;
  readonly detail?: (figures: OverviewFigures, format: OverviewFormat) => string | undefined;
  readonly measure?: (summary: PerformanceSummary) => number | undefined;
  readonly trend?: (summary: PerformanceSummary) => readonly number[];
  readonly risingIs?: Exclude<KpiDeltaTone, 'neutral'>;
  readonly shopWide?: true;
};

export const pendingOrdersOf = (orders: PerformanceReport['orders']): number =>
  Math.max(0, orders.created - orders.delivered - orders.returned - orders.canceled);

const statsCount =
  (countOf: (stats: DashboardStats) => number) =>
  ({ stats }: OverviewFigures, format: OverviewFormat): string | undefined =>
    stats === undefined ? undefined : format.count(countOf(stats));

const moneyMeasure =
  (amountOf: (summary: PerformanceSummary) => string | undefined) =>
  (summary: PerformanceSummary): number | undefined => {
    const amount = amountOf(summary);
    return amount === undefined ? undefined : toThousandths(amount);
  };

const seriesTrend =
  (metric: SeriesMetric) =>
  (summary: PerformanceSummary): readonly number[] =>
    summary.series.map((point) => (metric === 'sales' ? toThousandths(point.sales) : point[metric]));

const reportMoney =
  (amountOf: (report: PerformanceReport) => string | undefined) =>
  ({ report }: OverviewFigures, format: OverviewFormat): string | undefined => {
    const amount = report === undefined ? undefined : amountOf(report);
    return report === undefined || amount === undefined ? undefined : format.money(amount, report.currency);
  };

const reportCount =
  (key: string, countOf: (report: PerformanceReport) => number) =>
  ({ report }: OverviewFigures, format: OverviewFormat): string | undefined =>
    report === undefined ? undefined : format.counted(key, countOf(report));

const NO_COST_ACCESS = '—';

const stockMoney =
  (amountOf: (stock: StockValueTotals) => string | null) =>
  ({ stock }: OverviewFigures, format: OverviewFormat): string | undefined => {
    if (stock === undefined) return undefined;
    const amount = amountOf(stock);
    return amount === null ? NO_COST_ACCESS : format.money(amount, stock.currency);
  };

const stockUnits =
  (unitPriceOf: (stock: StockValueTotals) => PriceRange | null) =>
  ({ stock }: OverviewFigures, format: OverviewFormat): string | undefined => {
    if (stock === undefined) return undefined;
    const unitPrice = unitPriceOf(stock);
    return unitPrice === null
      ? format.counted('stockUnits', stock.onHand)
      : format.counted('stockUnitsAt', stock.onHand, { price: format.range(unitPrice, stock.currency) });
  };

export const OVERVIEW_TILES: readonly OverviewTile[] = [
  {
    key: 'products',
    icon: Package,
    tone: 'green',
    source: 'stats',
    value: statsCount((stats) => stats.activeProducts),
    shopWide: true,
  },
  {
    key: 'depots',
    icon: Warehouse,
    tone: 'amber',
    source: 'stats',
    value: statsCount((stats) => stats.locations),
    shopWide: true,
  },
  {
    key: 'charges',
    icon: DollarSign,
    tone: 'red',
    source: 'report',
    value: reportMoney((report) => report.charges.expenses),
    measure: moneyMeasure((summary) => summary.charges.expenses),
    risingIs: 'bad',
    shopWide: true,
  },
  {
    key: 'totalGains',
    icon: TrendingUp,
    tone: 'green',
    source: 'report',
    value: reportMoney((report) => report.sales),
    measure: moneyMeasure((summary) => summary.sales),
    trend: seriesTrend('sales'),
    detail: reportCount('deliveredOrders', (report) => report.orders.delivered),
  },
  {
    key: 'totalReturns',
    icon: RotateCcw,
    tone: 'amber',
    source: 'report',
    value: reportMoney((report) => report.returnedValue),
    measure: moneyMeasure((summary) => summary.returnedValue),
    trend: seriesTrend('returned'),
    risingIs: 'bad',
    detail: reportCount('returnedOrders', (report) => report.orders.returned),
  },
  {
    key: 'defectiveProducts',
    icon: PackageX,
    tone: 'red',
    source: 'report',
    value: reportMoney((report) => report.profit?.damageLoss),
    measure: moneyMeasure((summary) => summary.profit?.damageLoss),
    risingIs: 'bad',
    detail: reportCount('damagedUnits', (report) => report.damagedUnits),
  },
  {
    key: 'netProfit',
    icon: ChartColumn,
    tone: 'indigo',
    source: 'report',
    value: reportMoney((report) => report.profit?.net),
    measure: moneyMeasure((summary) => summary.profit?.net),
    shopWide: true,
  },
  {
    key: 'totalSales',
    icon: ShoppingCart,
    tone: 'blue',
    source: 'report',
    value: reportCount('salesCount', (report) => report.orders.created),
    measure: (summary) => summary.orders.created,
    trend: seriesTrend('created'),
  },
  {
    key: 'deliveredSales',
    icon: Truck,
    tone: 'green',
    source: 'report',
    value: reportCount('deliveredCount', (report) => report.orders.delivered),
    measure: (summary) => summary.orders.delivered,
    trend: seriesTrend('delivered'),
    detail: reportMoney((report) => report.sales),
  },
  {
    key: 'netSalesGain',
    icon: ChartLine,
    tone: 'indigo',
    source: 'report',
    value: reportMoney((report) => report.profit?.grossMargin),
    measure: moneyMeasure((summary) => summary.profit?.grossMargin),
    detail: reportCount('soldUnits', (report) => report.units),
  },
  {
    key: 'pendingSales',
    icon: Hourglass,
    tone: 'amber',
    source: 'report',
    value: reportCount('pendingCount', (report) => pendingOrdersOf(report.orders)),
    measure: (summary) => pendingOrdersOf(summary.orders),
    risingIs: 'bad',
  },
  {
    key: 'stockPurchaseValue',
    icon: Wallet,
    tone: 'indigo',
    source: 'stock',
    value: stockMoney((stock) => stock.purchaseValue),
    detail: stockUnits((stock) => stock.unitPurchasePrice),
  },
  {
    key: 'stockSaleValue',
    icon: Tags,
    tone: 'green',
    source: 'stock',
    value: stockMoney((stock) => stock.saleValue),
    detail: stockUnits((stock) => stock.unitSalePrice),
  },
];

const TILES_BY_KEY: ReadonlyMap<string, OverviewTile> = new Map(
  OVERVIEW_TILES.map((tile) => [tile.key, tile]),
);

export const tileOf = (key: string): OverviewTile => {
  const tile = TILES_BY_KEY.get(key);
  if (tile === undefined) throw new RangeError(`unknown overview tile ${key}`);
  return tile;
};
